// A-Session — Detects current trading session from UTC time. Zero AI. Zero API.

import { JARVIS_DNA, getActiveSession } from '../../dna/jarvisDNA';

// Best sessions for XAU/USD based on historical volatility
const SESSION_META = {
  ASIAN: {
    label:       'Asian Session',
    emoji:       '🌏',
    color:       '#6366f1',
    volatility:  'LOW',
    best_for:    'Range trading, accumulation',
    avoid:       'Breakout trades',
    tip:         'Watch for stop hunts before London open. Price often consolidates.',
  },
  LONDON: {
    label:       'London Session',
    emoji:       '🇬🇧',
    color:       '#10b981',
    volatility:  'HIGH',
    best_for:    'OB entries, BOS trades, trending moves',
    avoid:       'Counter-trend trades without strong confluence',
    tip:         'First 30 min (7:00-7:30 UTC) — displacement candles. OBs formed here are very strong.',
  },
  NEW_YORK: {
    label:       'New York Session',
    emoji:       '🗽',
    color:       '#f59e0b',
    volatility:  'HIGH',
    best_for:    'Continuation, news-driven moves, NY open range',
    avoid:       'Late NY (after 15:00 UTC) — liquidity dries up',
    tip:         'NY open (12:00-13:00 UTC) — often retraces London move then continues.',
  },
  OVERLAP: {
    label:       'London-NY Overlap',
    emoji:       '⚡',
    color:       '#ef4444',
    volatility:  'EXTREME',
    best_for:    'Strong trend trades with tight SL',
    avoid:       'Wide SL trades — whipsaws common',
    tip:         'Highest volume + volatility. Confirm with both session biases.',
  },
  OFF_HOURS: {
    label:       'Off Hours',
    emoji:       '🌙',
    color:       '#4b5563',
    volatility:  'MINIMAL',
    best_for:    'Analysis, planning next session trades',
    avoid:       'Live trading',
    tip:         'Use this time to review previous trades and prepare for next session.',
  },
};

export async function run() {
  // Read from JARVIS_DNA here, not at module top-level — see A_Fibonacci.js
  // for why (crashed the whole app in production build otherwise).
  const SESSIONS = JARVIS_DNA.trading.sessions;
  const now        = new Date();
  const utcHour    = now.getUTCHours();
  const utcMin     = now.getUTCMinutes();
  const utcTime    = `${String(utcHour).padStart(2,'0')}:${String(utcMin).padStart(2,'0')} UTC`;

  // Determine active session (overlap takes priority 12:00-13:00 UTC)
  let sessionId = 'OFF_HOURS';
  if (utcHour >= 12 && utcHour < 13)                    sessionId = 'OVERLAP';
  else if (utcHour >= SESSIONS.LONDON.utc_start   && utcHour < SESSIONS.LONDON.utc_end)   sessionId = 'LONDON';
  else if (utcHour >= SESSIONS.NEW_YORK.utc_start && utcHour < SESSIONS.NEW_YORK.utc_end) sessionId = 'NEW_YORK';
  else if (utcHour >= SESSIONS.ASIAN.utc_start    && utcHour < SESSIONS.ASIAN.utc_end)    sessionId = 'ASIAN';

  const meta = SESSION_META[sessionId];
  const cfg  = SESSIONS[sessionId] || {};

  // Time until next session
  const nextSessions = {
    ASIAN:    SESSIONS.ASIAN.utc_start,
    LONDON:   SESSIONS.LONDON.utc_start,
    NEW_YORK: SESSIONS.NEW_YORK.utc_start,
  };
  let nextSession = '', hoursToNext = 0;
  Object.entries(nextSessions).forEach(([s, h]) => {
    const diff = (h - utcHour + 24) % 24;
    if ((diff > 0 && diff < hoursToNext) || hoursToNext === 0) {
      hoursToNext = diff;
      nextSession = s;
    }
  });

  return {
    ok:          true,
    session:     sessionId,
    label:       meta.label,
    emoji:       meta.emoji,
    color:       meta.color,
    utcTime,
    utcHour,
    is_active:   sessionId !== 'OFF_HOURS',
    volatility:  meta.volatility,
    bias:        cfg.bias || 'NEUTRAL',
    best_for:    meta.best_for,
    avoid:       meta.avoid,
    tip:         meta.tip,
    next_session: nextSession,
    hours_to_next: hoursToNext,
    recommendation: sessionId === 'OFF_HOURS'
      ? `Not a trading session. ${nextSession ? `${nextSession} opens in ~${hoursToNext}h.` : ''} Use time for analysis.`
      : `${meta.label} active. ${meta.tip}`,
    agent:       'A-Session',
    requiresAI:  false,
    ts:          Date.now(),
  };
}
